import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import type { Member } from '@shared/api.interface';
import { MEMBER_STATUS_LABELS } from '@shared/api.interface';
import { formatDailyLimit, formatTypeHint, formatValidity, quotaUsage } from './member-row-format';

interface MemberListViewProps {
  members: Member[];
  statusBadgeClass: Record<Member['status'], string>;
  getUsagePercent: (member: Member) => number;
  getValidityMonths: (member: Member) => number | undefined;
  onOpenDetail: (passcode: string) => void;
  onAdjust: (member: Member) => void;
  onEdit: (member: Member) => void;
  onRevoke: (passcode: string) => void;
}

// 未激活、已作废由系统流程产生：不能调整额度；已作废不能再作废。按钮保留但变灰，保证每行对齐
const cannotAdjust = (status: Member['status']) => status === 'inactive' || status === 'revoked';

function StatusPill({ member, className }: { member: Member; className: string }) {
  return (
    <span className={`inline-flex min-w-14 justify-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {MEMBER_STATUS_LABELS[member.status]}
    </span>
  );
}

function QuotaBar({ member, percent }: { member: Member; percent: number }) {
  const { used, limit } = quotaUsage(member);
  return (
    <div className="flex items-center gap-2 whitespace-nowrap">
      <span aria-hidden className="h-1.5 w-14 shrink-0 overflow-hidden rounded-full bg-muted">
        <span className="block h-full rounded-full bg-cta" style={{ width: `${percent}%` }} />
      </span>
      <span className="text-sm tabular-nums"><b className="font-semibold">{used}</b> / {limit}</span>
    </div>
  );
}

/** 配额使用第二行：累计领取；年卡预支了下周期时一并写出 */
function quotaHint(member: Member): ReactNode {
  const borrowed = member.quota_mode !== 'total' ? member.next_cycle_borrowed ?? 0 : 0;
  return (
    <>
      累计领取 {member.total_redemptions} 次
      {borrowed > 0 && <span className="font-semibold text-sky-800" title="已预支下周期的次数"> · 预支 {borrowed}</span>}
    </>
  );
}

function DailyUsage({ member }: { member: Member }) {
  return (
    <span className="whitespace-nowrap text-sm tabular-nums">
      <b className="font-semibold">{member.today_used}</b> / {formatDailyLimit(member.daily_limit)}
    </span>
  );
}

function Actions({ member, onAdjust, onEdit, onRevoke, align }: {
  member: Member;
  onAdjust: () => void;
  onEdit: () => void;
  onRevoke: () => void;
  align: 'end' | 'start';
}) {
  const adjustDisabled = cannotAdjust(member.status);
  const revoked = member.status === 'revoked';
  return (
    <div className={`grid grid-cols-[auto_auto_auto] gap-0.5 ${align === 'end' ? 'justify-end' : 'justify-start'}`}>
      <Button size="sm" variant="ghost" className="h-8 px-2.5" disabled={adjustDisabled}
        title={adjustDisabled ? '未激活或已作废的码不能调整额度' : undefined} onClick={onAdjust}>
        调额度
      </Button>
      <Button size="sm" variant="ghost" className="h-8 px-2.5" onClick={onEdit}>
        编辑
      </Button>
      <Button size="sm" variant="ghost" className="h-8 px-2.5 text-red-700 hover:bg-red-50 hover:text-red-800" disabled={revoked}
        title={revoked ? '已经作废' : undefined} onClick={onRevoke}>
        作废
      </Button>
    </div>
  );
}

/** 两行单元格：上面主信息，下面灰色辅助信息 */
function Cell({ main, hint, mono }: { main: ReactNode; hint?: ReactNode; mono?: boolean }) {
  return (
    <>
      <div className="whitespace-nowrap text-sm leading-snug">{main}</div>
      {hint !== undefined && <div className={`mt-0.5 whitespace-nowrap text-xs leading-snug text-muted-foreground ${mono ? 'font-mono' : ''}`}>{hint}</div>}
    </>
  );
}

export function MemberListView(props: MemberListViewProps) {
  const { members, statusBadgeClass, getUsagePercent, getValidityMonths, onOpenDetail, onAdjust, onEdit, onRevoke } = props;

  const codeButton = (member: Member) => (
    <button
      type="button"
      onClick={() => onOpenDetail(member.passcode)}
      className="rounded font-mono text-sm tracking-wide text-foreground underline-offset-4 hover:text-cta hover:underline"
    >
      {member.passcode}
    </button>
  );

  return (
    <>
      {/* 电脑：8 列，一屏放下；窗口过窄时才在表格内部横向滚动 */}
      <div className="hidden overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--border)] lg:block">
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="bg-background text-left text-sm text-muted-foreground">
              <tr>
                <th className="whitespace-nowrap py-2.5 pl-4 pr-2.5 font-medium">兑换码 / 订单号</th>
                <th className="whitespace-nowrap px-2.5 py-2.5 font-medium">会员类型</th>
                <th className="whitespace-nowrap px-2.5 py-2.5 text-center font-medium">状态</th>
                <th className="whitespace-nowrap px-2.5 py-2.5 font-medium text-orange-900">配额使用</th>
                <th className="whitespace-nowrap px-2.5 py-2.5 font-medium text-orange-900">今日已领</th>
                <th className="whitespace-nowrap px-2.5 py-2.5 font-medium">有效期</th>
                <th className="whitespace-nowrap px-2.5 py-2.5 font-medium">渠道 / 备注</th>
                <th className="whitespace-nowrap py-2.5 pl-2.5 pr-4 text-right font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {members.map(member => {
                const validity = formatValidity(member, getValidityMonths(member));
                return (
                  <tr key={member.passcode} className="h-16 align-middle hover:bg-orange-50/30">
                    <td className="py-2.5 pl-4 pr-2.5"><Cell main={codeButton(member)} hint={member.order_no || '无订单号'} mono /></td>
                    <td className="px-2.5 py-2.5"><Cell main={member.member_type} hint={formatTypeHint(member)} /></td>
                    <td className="px-2.5 py-2.5 text-center"><StatusPill member={member} className={statusBadgeClass[member.status]} /></td>
                    <td className="px-2.5 py-2.5">
                      <Cell
                        main={<QuotaBar member={member} percent={getUsagePercent(member)}  />}
                        hint={quotaHint(member)}
                      />
                    </td>
                    <td className="px-2.5 py-2.5"><DailyUsage member={member} /></td>
                    <td className="px-2.5 py-2.5"><Cell main={validity.main} hint={validity.hint} /></td>
                    <td className="max-w-[140px] px-2.5 py-2.5">
                      <div className="truncate text-sm leading-snug" title={member.source_channel || undefined}>{member.source_channel || '-'}</div>
                      <div className="mt-0.5 truncate text-xs leading-snug text-muted-foreground" title={member.remark || undefined}>{member.remark || '无备注'}</div>
                    </td>
                    <td className="py-2.5 pl-2.5 pr-3">
                      <Actions member={member} align="end" onAdjust={() => onAdjust(member)} onEdit={() => onEdit(member)} onRevoke={() => onRevoke(member.passcode)} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {members.length === 0 && <div className="py-10 text-center text-muted-foreground">暂无符合筛选条件的会员</div>}
      </div>

      {/* 手机/窄屏：一个会员一张卡片，上下滑动 */}
      <div className="space-y-2.5 lg:hidden">
        {members.length === 0 && (
          <div className="rounded-2xl bg-white py-10 text-center text-muted-foreground shadow-[0_0_0_1px_var(--border)]">暂无符合筛选条件的会员</div>
        )}
        {members.map(member => {
          const validity = formatValidity(member, getValidityMonths(member));
          const extra = [member.order_no ? `订单 ${member.order_no}` : '', member.source_channel, member.remark].filter(Boolean).join(' · ');
          return (
            <article key={member.passcode} className="space-y-2.5 rounded-2xl bg-white px-3.5 py-3 shadow-[0_0_0_1px_var(--border)]">
              <div className="flex items-center justify-between gap-2">
                {codeButton(member)}
                <StatusPill member={member} className={statusBadgeClass[member.status]} />
              </div>
              <div className="grid grid-cols-2 gap-x-3 gap-y-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">配额使用 · {member.member_type}</p>
                  <QuotaBar member={member} percent={getUsagePercent(member)}  />
                  <p className="text-xs text-muted-foreground">{quotaHint(member)}</p>
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">今日已领</p>
                  <DailyUsage member={member} />
                </div>
                <div className="col-span-2 min-w-0">
                  <p className="text-xs text-muted-foreground">有效期</p>
                  <p className="text-sm">{validity.main}{validity.hint ? <span className="text-muted-foreground"> · {validity.hint}</span> : null}</p>
                </div>
                {extra && <p className="col-span-2 break-words text-xs text-muted-foreground">{extra}</p>}
              </div>
              <div className="border-t border-dashed pt-2">
                <Actions member={member} align="start" onAdjust={() => onAdjust(member)} onEdit={() => onEdit(member)} onRevoke={() => onRevoke(member.passcode)} />
              </div>
            </article>
          );
        })}
      </div>
    </>
  );
}
